import { Box, useTheme } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import {
    type AddArticleParamsType,
    type DossierArticle,
    DossierRoutingContext,
    DossierValidatorStatus,
    REFRESH_CURRENT_DOSSIER_CTX,
    RESET_DOS_DOCS_CTX,
    VALIDATOR_TYPE_IMPOTS,
    useCurrentDossierCtx,
    useCurrentDossierCtxDispatch,
    useDossierArticle,
    useDossierDocumentsCtxDispatch,
    useDossierProcesses,
    useDossierProcessesActionsAndRender,
    useDossierValidatorsCtx,
} from '@europrocurement/l2d-modules';
import { ConfirmationModal, useModal } from '@europrocurement/flexy-components';
import { enqueueSnackbar } from 'notistack';
import { useHandleError } from '@europrocurement/l2d-hooks';
import { useNavigate } from 'react-router';
import { NestedDossierProcessesOpenSearch } from '@europrocurement/l2d-domain';
import type { IconName } from '@fortawesome/fontawesome-svg-core';
import { DossierArticleProcessMenuItemProps } from '../DossierArticleProcessMenu/DossierArticleProcessMenu';
import DossierProcessesFooter from '../DossierProcessesFooter/DossierProcessesFooter';
import AddTaxForm, { AddTaxFormData } from '../AddTaxForm/AddTaxForm';
import UpdateValidatorForm from '../UpdateValidatorForm/UpdateValidatorForm';
import DossierArticlesProcessesTaxList from '../DossierArticlesProcessesTaxList/DossierArticlesProcessesTaxList';
import { DossierProcessesArticlesProps } from '../DossierProcesses/DossierProcessesType';
import DossierProcessesBulkActionsBar from '../DossierProcessesBulkActionsBar/DossierProcessesBulkActionsBar';
import useDossierBulkActions from '../../hooks/useDossierBulkActions';

const DossierImpotProcesses: React.FunctionComponent<DossierProcessesArticlesProps> = function ({
    articles,
}) {
    const validatorTypeId = VALIDATOR_TYPE_IMPOTS;
    const {
        findProcessByArticle,
        canCreateValidatorProcess,
        createValidatorProcess,
        getProcessStatusByArticle,
    } = useDossierProcesses();
    const { palette } = useTheme();
    const { addArticle, deleteArticle } = useDossierArticle();
    const { dossier } = useCurrentDossierCtx();
    const dispatch = useCurrentDossierCtxDispatch();
    const dossierDocumentsdispatch = useDossierDocumentsCtxDispatch();
    const navigate = useNavigate();
    const { getDossierRoute } = React.useContext(DossierRoutingContext);

    const refreshDossier = useCallback(
        (dossierId?: number) => {
            if (dispatch) {
                dispatch({ type: REFRESH_CURRENT_DOSSIER_CTX });
            }
            if (dossierDocumentsdispatch) {
                dossierDocumentsdispatch({ type: RESET_DOS_DOCS_CTX });
            }
            if (dossierId) {
                const route = getDossierRoute(dossierId as number);
                if (route) {
                    navigate(route);
                }
            }
        },
        [dispatch, dossierDocumentsdispatch, getDossierRoute, navigate],
    );

    const { modalActions } = useModal();
    const { apiErrorMessageSnackbar } = useHandleError();
    const { getDossierValidatorById } = useDossierValidatorsCtx();

    const [processing, setProcessing] = useState<boolean>(false);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const { actionUpdateValidatorStatus, handleUpdateStatusChange } =
        useDossierProcessesActionsAndRender();

    const handelDeleteArticle = (articleId: string) => {
        setProcessing(true);

        deleteArticle(articleId)
            .then(() => {
                setProcessing(false);
                modalActions.close();
            })
            .catch((err) =>
                apiErrorMessageSnackbar(
                    err,
                    'Une erreur est survenue lors de la suppression de la démarche',
                ),
            );

        setProcessing(false);
        refreshDossier();
    };

    const handleStatusChange = async (
        process: NestedDossierProcessesOpenSearch,
        newStatus: DossierValidatorStatus,
    ) => {
        try {
            await handleUpdateStatusChange(process, newStatus.code);
        } catch (e) {
            console.error(e);
            enqueueSnackbar('Une erreur est survenue lors de la mise à jour de votre démarche', {
                variant: 'error',
            });
        } finally {
            modalActions.close();
            refreshDossier();
        }
    };

    const canCreateProcess = useMemo(
        () => (dossier ? canCreateValidatorProcess(dossier, validatorTypeId) : false),
        [dossier, canCreateValidatorProcess, validatorTypeId],
    );

    const { canDissociate, canMove, handleDissociate, handleMove } = useDossierBulkActions({
        selectedIds,
        refreshDossier,
    });

    const getItemActions = (item: DossierArticle) => {
        if (!dossier) {
            return [];
        }
        const actions: Array<DossierArticleProcessMenuItemProps> = [];
        const statusProcess = getProcessStatusByArticle(dossier, validatorTypeId, item.id);
        const process = findProcessByArticle(dossier, [validatorTypeId], item.id);
        const statuses = process
            ? getDossierValidatorById(process.validatorId)?.statuses || []
            : [];

        if (['pending'].includes(statusProcess) && canCreateProcess) {
            if (process) {
                actions.push(
                    actionUpdateValidatorStatus(() => {
                        modalActions.call(
                            <UpdateValidatorForm
                                processing={processing}
                                process={process}
                                onStatusChanged={(newStatus) => {
                                    handleStatusChange(process, newStatus);
                                }}
                                statuses={statuses}
                            />,
                        );
                    }),
                );
            }

            actions.push({
                label: 'Supprimer la démarche',
                icon: 'trash',
                onClick: (article: DossierArticle) => {
                    modalActions.call(
                        <ConfirmationModal
                            color={palette.error}
                            messages={{
                                question: 'Confirmer cette action ?',
                                detail: {
                                    message: 'Êtes vous sur de vouloir supprimer la démarche ?',
                                    severity: 'error',
                                },
                                validation: 'Supprimer',
                            }}
                            actionOnValidation={() => handelDeleteArticle(article.id.toString())}
                            actionOnCancellation={() => modalActions.close()}
                        />,
                    );
                },
            });

            if (canMove) {
                actions.push({
                    label: 'Déplacer',
                    icon: 'file-import' as IconName,
                    onClick: () => handleMove([item.id]),
                });
            }
            if (canDissociate) {
                actions.push({
                    label: 'Dissocier',
                    icon: 'folders' as IconName,
                    onClick: () => handleDissociate([item.id]),
                });
            }
        } else if (['ongoing', 'default', 'rescheduled'].includes(statusProcess)) {
            actions.push({
                label: 'Demander une signature électronique',
                icon: 'file-signature',
                onClick: (article: DossierArticle) => {
                    enqueueSnackbar(`Non implémenté signature ${article.label}`, {
                        variant: 'error',
                    });
                },
            });

            actions.push({
                label: 'Activer option DCE',
                icon: 'check-square',
                onClick: (article: DossierArticle) => {
                    enqueueSnackbar(`Non implémenté option DCE ${article.label}`, {
                        variant: 'error',
                    });
                },
            });

            if (process) {
                actions.push(
                    actionUpdateValidatorStatus(() => {
                        modalActions.call(
                            <UpdateValidatorForm
                                processing={processing}
                                process={process}
                                onStatusChanged={(newStatus) => {
                                    handleStatusChange(process, newStatus);
                                }}
                                statuses={statuses}
                            />,
                        );
                    }),
                );
            }
        }
        return actions;
    };

    const onSubmitAddTax = async (data: AddTaxFormData) => {
        if (!dossier) {
            enqueueSnackbar('Dossier non trouvé', { variant: 'error' });
            return;
        }
        setProcessing(true);

        try {
            const articleId = await addArticle({
                prestationId: data.prestationId,
                label: data.title,
                factArticleId: data.articleId,
            } as AddArticleParamsType);
            await createValidatorProcess(dossier.id as number, [articleId]);
            modalActions.close();
            refreshDossier();
        } catch (err) {
            apiErrorMessageSnackbar(
                err as Error,
                "Une erreur est survenue lors de l'ajout de l'enregistrement",
            );
        } finally {
            setProcessing(false);
        }
    };

    const handleAddTax = useCallback(() => {
        if (!dossier) {
            enqueueSnackbar('Dossier non trouvé', { variant: 'error' });
            return;
        }
        modalActions.call(
            <AddTaxForm
                onCancel={() => {
                    setProcessing(false);
                    modalActions.close();
                }}
                onSubmit={onSubmitAddTax}
                dossier={dossier}
            />,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossier, modalActions, processing]);

    return (
        <Box sx={{ gap: '16px', display: 'grid' }}>
            {dossier && (
                <>
                    <DossierProcessesBulkActionsBar
                        selectedIds={selectedIds}
                        onClearSelection={() => setSelectedIds([])}
                        canDissociate={canDissociate}
                        onDissociate={handleDissociate}
                        canMove={canMove}
                        onMove={handleMove}
                    />
                    <DossierArticlesProcessesTaxList
                        articles={articles}
                        dossier={dossier}
                        validatorTypeId={validatorTypeId}
                        legalAnnoucements={[]}
                        getItemActions={getItemActions}
                        selectedIds={selectedIds}
                        setSelectedIds={setSelectedIds}
                    />
                </>
            )}

            {canCreateProcess && (
                <DossierProcessesFooter
                    handleAddItem={handleAddTax}
                    addItemTextBtn="Ajouter un enregistrement"
                    canAddItem
                />
            )}
        </Box>
    );
};

export default DossierImpotProcesses;
